import { useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';

import { useNavigate } from 'react-router-dom';

import { usePageTitle } from '../../../Hooks/usePageTitle';

import { getSubscriptionPlans, createPaymentIntent, subscribeToPlans } from '../../../Services/User/SubscriptionPlans';

import { showToast } from '../../../Components/Toast/toast';



import InnerBanner from '../../../Components/InnerBanner/InnerBanner';

import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';

import StripePayment from '../Payment/StripePayment';

import './SubscriptionPlans.css';

import CustomButton from '../../../Components/CustomButton';



const SubscriptionPlans = () => {

  const pageTitle = useGoogleTranslate('Subscription Plans');

  const loadingLabel = useGoogleTranslate('Loading subscription plans...');

  const noPlansLabel = useGoogleTranslate('No subscription plans available');

  const viewLogsLabel = useGoogleTranslate('View Subscription Logs');

  const processingLabel = useGoogleTranslate('Processing...');

  const choosePlanLabel = useGoogleTranslate('Choose Plan');

  const monthlyLabel = useGoogleTranslate('Monthly');

  const fourMonthsLabel = useGoogleTranslate('4 Months');

  const fourMonthsPromoLabel = useGoogleTranslate('Pay for 3 months, get 1 month free');

  const oneMonthFreeLabel = useGoogleTranslate('1 month free');

  const perMonthLabel = useGoogleTranslate('per month');

  const subscriptionPlanLabel = useGoogleTranslate('Subscription Plan');

  const featureFavoriteProfiles = useGoogleTranslate('Can favorite other profiles');

  const featureMessages = useGoogleTranslate('Send and receive messages');

  const featureGames = useGoogleTranslate('Play all games');

  const featureVirtualDate = useGoogleTranslate('Virtual date (Watch together)');

  const featureCoins = useGoogleTranslate('Purchase coins to send gifts and greeting cards');

  const featureViews = useGoogleTranslate('See who viewed you');

  const featureFavorites = useGoogleTranslate('See who favorited you');

  const premiumFeatureIncognito = useGoogleTranslate(

    'Can choose to go in incognito mode and browse profiles anonymously without other users knowing that you viewed your profile'

  );

  const premiumFeatureAutoReply = useGoogleTranslate(

    'Can set up auto-reply messaging so you can stay connected while away from the app'

  );

  const premiumFeatureBadge = useGoogleTranslate(

    'Will have a premium badge on your profile so it stands out like a status symbol'

  );

  const premiumFeatureVisibility = useGoogleTranslate(

    "Can choose who sees you so you're able to manage your profile's visibility (you can select to only be seen by those that you like first)"

  );

  const premiumFeatureMessageControl = useGoogleTranslate(

    'Can set it up to where you only receive messages from those you message first (control who messages you first)'

  );

  const premiumFeatureTopSearch = useGoogleTranslate('Profile appears at the top of searches');

  const premiumIncludesStandard = useGoogleTranslate('Includes everything that is in the Standard Plan plus');

  

  // Features showcase labels

  const featuresTitle = useGoogleTranslate('Premium Features That Set Us Apart');

  const virtualDateTitle = useGoogleTranslate('Virtual Dates');

  const virtualDateDesc = useGoogleTranslate('Connect face-to-face with Watch Together feature. Enjoy movies, games, and shared experiences from anywhere.');

  const giftSendingTitle = useGoogleTranslate('Gift Sending');

  const giftSendingDesc = useGoogleTranslate('Send virtual gifts and greeting cards to show you care. Choose from coins, flowers, and more.');

  const gamesTitle = useGoogleTranslate('Interactive Games');

  const gamesDesc = useGoogleTranslate('Play fun games together like Tic-Tac-Toe and break the ice with matches.');



  usePageTitle(pageTitle);

  const navigate = useNavigate();

  const [showPayment, setShowPayment] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);

  const [clientSecret, setClientSecret] = useState(null);

  const [isCreatingIntent, setIsCreatingIntent] = useState(false);



  const { data: response, isLoading } = useQuery({

    queryKey: ['subscription-plans'],

    queryFn: getSubscriptionPlans,

  });



  const getPlanCategory = (plan) => {
    const planType = (plan.plan_type || plan.type || plan.name || plan.slug || '').toLowerCase();
    const slug = (plan.slug || '').toLowerCase();

    if (planType.includes('premium') || slug.includes('premium')) {
      return 'Premium';
    }

    if (planType.includes('standard') || slug.includes('standard')) {
      return 'Standard';
    }

    return null;
  };

  const getPlanInterval = (plan) => (plan.duration || plan.interval || '').toLowerCase();

  // Categorize plans by type (Standard, Premium) and duration
  const categorizePlans = (plans) => {

    const categories = {};

    const allowedIntervals = ['month', 'monthly', '4months'];

    const filteredPlans = plans.filter((plan) => {
      const category = getPlanCategory(plan);
      const duration = getPlanInterval(plan);

      return category && allowedIntervals.includes(duration);
    });

    filteredPlans.forEach((plan) => {
      const categoryName = getPlanCategory(plan);

      if (!categories[categoryName]) {
        categories[categoryName] = {
          name: categoryName,
          plans: [],
        };
      }

      categories[categoryName].plans.push(plan);
    });



    // Sort plans within each category by duration (month, 4months)

    Object.keys(categories).forEach(categoryName => {

      categories[categoryName].plans.sort((a, b) => {

        const durationOrder = { 'month': 1, 'monthly': 1, '4months': 2 };

        const aDuration = (a.duration || a.interval || '').toLowerCase();

        const bDuration = (b.duration || b.interval || '').toLowerCase();

        return (durationOrder[aDuration] || 99) - (durationOrder[bDuration] || 99);

      });

    });



    return Object.values(categories).sort((a, b) => {
      if (a.name === 'Standard') return -1;
      if (b.name === 'Standard') return 1;
      return a.name.localeCompare(b.name);
    });

  };



  const handleChoosePlan = async (plan) => {

    try {

      setIsCreatingIntent(true);

      setSelectedPlan(plan);



      // Create payment intent

      const response = await createPaymentIntent(parseFloat(plan.price));



      if (response?.client_secret) {

        setClientSecret(response.client_secret);

        setShowPayment(true);

      } else {

        showToast('Failed to initialize payment. Please try again.', 'error');

        setIsCreatingIntent(false);

      }

    } catch (error) {

      console.error('Error creating payment intent:', error);

      showToast(error?.message || 'Failed to initialize payment', 'error');

      setIsCreatingIntent(false);

    }

  };



  const handlePaymentSuccess = async (paymentIntent) => {

    try {

      // Verify payment intent status

      if (paymentIntent.status !== 'succeeded') {

        showToast('Payment not completed. Please try again.', 'error');

        return;

      }



      // Add a small delay to ensure Stripe has fully processed the charge

      await new Promise(resolve => setTimeout(resolve, 1000));



      // Call subscribe endpoint with payment_intent_id and payment_method_id

      const response = await subscribeToPlans(

        selectedPlan.slug,

        paymentIntent.id,

        paymentIntent.payment_method // Pass payment_method_id if available

      );



      if (response?.status) {

        showToast('Subscription successful!', 'success');

        // Navigate to subscription logs or success page

        setTimeout(() => {

          navigate('/subscription-logs');

        }, 500);

      } else {

        showToast(response?.message || 'Subscription failed. Please contact support.', 'error');

      }

    } catch (error) {

      console.error('Error subscribing:', error);

      const errorMessage = error?.message || error?.error || 'Subscription failed. Please contact support.';

      showToast(errorMessage, 'error');

    } finally {

      setShowPayment(false);

      setSelectedPlan(null);

      setClientSecret(null);

      setIsCreatingIntent(false);

    }

  };



  const handlePaymentError = (error) => {

    console.error('Payment error:', error);

    showToast(error?.message || 'Payment failed', 'error');

    setIsCreatingIntent(false);

  };



  const handleBackFromPayment = () => {

    setShowPayment(false);

    setSelectedPlan(null);

    setClientSecret(null);

    setIsCreatingIntent(false);

  };



  const renderPlanCard = (plan, savingsInfo) => {

    if (!plan) return null;



    // Get duration label

    const duration = getPlanInterval(plan);
    let durationLabel = duration;

    if (duration === 'month' || duration === 'monthly') {
      durationLabel = monthlyLabel;
    } else if (duration === '4months' || duration.includes('4 month')) {
      durationLabel = fourMonthsLabel;
    }

    const isFourMonthPlan = duration === '4months' || duration.includes('4 month');



    return (

      <Col xs={12} sm={6} md={6} lg={6} className="mb-3">

        <div className="plan-card justify-content-between">

          <div>

            <div className="plan-header">
              <h4 className="plan-duration-title mb-0">{durationLabel}</h4>
            </div>

            <p className="plan-description">

              {plan.description || plan.name || 'Lorem ipsum dolor sit amet consectetur adipiscing elit.'}

            </p>

            <div className="text-start">

              <div className="plan-price">${Number(plan.price).toFixed(2)}</div>

              {isFourMonthPlan && (
                <>
                  <div className="plan-free-month">{oneMonthFreeLabel}</div>
                  <div className="plan-promo text-muted small mt-1">
                    {plan.promo_message || fourMonthsPromoLabel}
                  </div>
                </>
              )}

              {savingsInfo && (
                <div className="plan-savings">
                  {`Save $${savingsInfo.amount} 🔥`}
                </div>
              )}

            </div>

          </div>

          <div className="text-start">

            <CustomButton

              variant="primary"

              className=""

              onClick={() => handleChoosePlan(plan)}

              disabled={isCreatingIntent}

              loading={isCreatingIntent && selectedPlan?.id === plan.id}

            >

              {isCreatingIntent && selectedPlan?.id === plan.id ? processingLabel : choosePlanLabel}

            </CustomButton>

          </div>

        </div>

      </Col>

    );

  };



  const renderPlanCategory = (category) => {

    if (!category.plans || category.plans.length === 0) return null;



    const isPremiumCategory = category.name.toLowerCase() === 'premium';



    const standardFeatures = [

      featureFavoriteProfiles,

      featureMessages,

      featureGames,

      featureVirtualDate,

      featureCoins,

      featureViews,

      featureFavorites,

    ];



    const premiumFeatures = [

      premiumFeatureIncognito,

      premiumFeatureAutoReply,

      premiumFeatureBadge,

      premiumFeatureVisibility,

      premiumFeatureMessageControl,

      premiumFeatureTopSearch,

    ];



    // Find monthly / 4-month plans within this category

    const getDurationKey = (plan) => {

      const d = (plan.duration || plan.interval || '').toLowerCase();

      if (d === 'month' || d === 'monthly') return 'month';

      if (d === '4months' || d.includes('4 month')) return 'fourMonths';

      return null;

    };



    const monthlyPlan = category.plans.find((p) => getDurationKey(p) === 'month');

    const fourMonthPlan = category.plans.find((p) => getDurationKey(p) === 'fourMonths');



    const parsePrice = (plan) => {

      if (!plan) return null;

      const n = parseFloat(plan.price);

      return Number.isNaN(n) ? null : n;

    };



    const monthlyPrice = parsePrice(monthlyPlan);

    const fourMonthPrice = parsePrice(fourMonthPlan);



    // Pre-compute savings per plan id (4-month plan = 1 month free vs paying monthly)

    const savingsByPlanId = {};



    if (monthlyPrice != null && fourMonthPrice != null) {

      const raw = monthlyPrice * 4 - fourMonthPrice;

      if (raw > 0) {

        savingsByPlanId[fourMonthPlan.id] = {

          amount: raw.toFixed(2),

        };

      }

    }



    return (

      <div key={category.name} className="plan-category mb-5">

        <h3 className="plan-category-title">{category.name} {subscriptionPlanLabel}</h3>

        {isPremiumCategory && <p className="premium-includes-text">{premiumIncludesStandard}</p>}

        <ul className="plan-features">

          {(isPremiumCategory ? premiumFeatures : standardFeatures).map((feature) => (

            <li key={feature}>{feature}</li>

          ))}

        </ul>

        <div className="plan-cards-wrapper">

          <Row>

            {category.plans.map((plan) =>

              renderPlanCard(plan, savingsByPlanId[plan.id])

            )}

          </Row>

        </div>

      </div>

    );

  };



  if (isLoading) {

    return (

      <>

        <InnerBanner heading={pageTitle} />

        <section className="py-sm-5 py-4">

          <Container>

            <Row>

              <Col xs={12} className="text-center">

                <Spinner animation="border" role="status">

                  <span className="visually-hidden">Loading...</span>

                </Spinner>

                <p className="mt-3">{loadingLabel}</p>

              </Col>

            </Row>

          </Container>

        </section>

      </>

    );

  }



  if (!response?.data || response.data.length === 0) {

    return (

      <>

        <InnerBanner heading={pageTitle} />

        <section className="py-sm-5 py-4">

          <Container>

            <Row>

              <Col xs={12} className="text-center">

                <p className="mt-3">{noPlansLabel}</p>

              </Col>

            </Row>

          </Container>

        </section>

      </>

    );

  }



  const categorizedPlans = categorizePlans(response.data);



  // Show payment screen if payment is initiated

  if (showPayment && selectedPlan && clientSecret) {

    return (

      <StripePayment

        onBack={handleBackFromPayment}

        amount={parseFloat(selectedPlan.price)}

        onSuccess={handlePaymentSuccess}

        onError={handlePaymentError}

        clientSecret={clientSecret}

      />

    );

  }



  return (

    <>

      <InnerBanner heading={pageTitle} />

      <section className="py-sm-5 py-4">

        <Container>

          <Row>

            <Col xs={12} className="mb-4 text-center">

              <Button

                variant="primary"

                className="subscription-logs-btn"

                onClick={() => navigate('/subscription-logs')}

              >

                {viewLogsLabel}

              </Button>

            </Col>

          </Row>

          <Row>

            <Col xs={12}>

              <div className="subscription-plans-container">

                {categorizedPlans.map(category => renderPlanCategory(category))}

              </div>

            </Col>

          </Row>

        </Container>

      </section>

    </>

  );

};



export default SubscriptionPlans;

